import { AlertCircle, Check, SearchLg } from '@untitledui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Selection } from 'react-aria-components';
import {
  Dialog as AriaDialog,
  Header as AriaHeader,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
  Section as AriaSection,
} from 'react-aria-components';
import { useParams } from 'react-router-dom';

import { useAuthStatus } from '@/allauth_auth/hooks';
import { useOverlayBackdrop } from '@/contexts/OverlayBackdropContext';
import { useOverlayOrder } from '@/contexts/OverlayOrderContext';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { useOpenSlideout } from '@/lib/crm/hooks/useOpenSlideout';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { logger } from '@/lib/logger';
import { track } from '@/lib/mixpanel';
import type { EntityResult, EntityType } from '@/lib/search';
import {
  addRecentSearch,
  getRecentSearches,
  useEntitySearch,
} from '@/lib/search';
import { cx } from '@/utils/cx';

import { useGlobalSearch } from './useGlobalSearch';

/**
 * GlobalSearch - Command palette for searching across CRM entities.
 *
 * Features:
 * - Cmd+K/Ctrl+K keyboard shortcut to open
 * - Search input with debouncing via useEntitySearch
 * - Filter chips: All | Contacts | Companies | Deals (single-select)
 * - Grouped results by entity type with keyboard navigation
 * - Recent searches when no query
 * - Loading, error, empty, and no results states
 */
export function GlobalSearch() {
  const [, authStatus] = useAuthStatus();
  const { isOpen, close } = useGlobalSearch(!authStatus.isAuthenticated);
  const { orgId } = useParams<{ orgId: string }>();
  // App-level click: opening a search result REPLACES any open slideouts.
  const openSlideout = useOpenSlideout();

  const [query, setQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<EntityType[] | undefined>(
    undefined
  );

  // Reset query when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    } else {
      logger.info('Global search opened', { component: 'GlobalSearch' });
      track('global_search_opened');
    }
  }, [isOpen]);

  // Log modal close
  useEffect(() => {
    return () => {
      if (isOpen) {
        logger.info('Global search closed', { component: 'GlobalSearch' });
      }
    };
  }, [isOpen]);

  // Search data from API
  const { results, resultQuery, resultTypes, isLoading, error, refetch } =
    useEntitySearch(query, {
      types: selectedTypes,
    });

  // Track search queries when results arrive
  // We use resultQuery and resultTypes (the parameters that produced the results)
  // to ensure queryLength and filter match the actual search, not newer values
  // the user may have changed while the request was in flight.
  const lastTrackedKey = useRef('');
  useEffect(() => {
    // resultQuery and resultTypes are only set when results arrive, so they
    // accurately represent the parameters that produced the current result count
    if (results && resultQuery) {
      const filter = resultTypes?.[0] ?? 'all';
      const trackingKey = `${resultQuery}:${filter}`;
      if (trackingKey !== lastTrackedKey.current) {
        lastTrackedKey.current = trackingKey;
        const resultCount =
          results.contacts.length +
          results.companies.length +
          results.deals.length;
        track('global_search_query', {
          queryLength: resultQuery.length,
          resultCount,
          filter,
        });
      }
    }
  }, [results, resultQuery, resultTypes]);

  // Overlay stacking system
  const overlayOrder = useOverlayOrder(isOpen ? 1 : 0);
  useOverlayBackdrop(isOpen);
  const zIndex = 400 + overlayOrder * 100;

  // Recent searches for empty state
  const recentSearches = useMemo(() => {
    if (!orgId || query.length >= 2) {
      return [];
    }
    return getRecentSearches(Number(orgId), 'global');
  }, [orgId, query]);

  // Handle result selection
  const handleSelect = (entity: EntityResult) => {
    logger.info('Search result selected', {
      entityId: entity.entity_id,
      entityType: entity.type,
      component: 'GlobalSearch',
    });
    track('global_search_result_selected', {
      entityType: entity.type,
      hasQuery: query.length >= 2,
    });
    openSlideout(entity.entity_id);
    if (orgId) {
      addRecentSearch(Number(orgId), 'global', entity);
    }
    close();
  };

  // Build grouped results list for AriaListBox
  const groupedItems = useMemo(() => {
    if (!results) {
      return [];
    }

    const groups: Array<{
      type: 'contacts' | 'companies' | 'deals';
      label: string;
      items: EntityResult[];
    }> = [];

    if (results.contacts.length > 0) {
      groups.push({
        type: 'contacts',
        label: 'Contacts',
        items: results.contacts,
      });
    }
    if (results.companies.length > 0) {
      groups.push({
        type: 'companies',
        label: 'Companies',
        items: results.companies,
      });
    }
    if (results.deals.length > 0) {
      groups.push({ type: 'deals', label: 'Deals', items: results.deals });
    }

    return groups;
  }, [results]);

  // Build flat items list with keys for AriaListBox selection
  const flatItems = useMemo(() => {
    return groupedItems.flatMap(group => group.items);
  }, [groupedItems]);

  // Handle selection change from AriaListBox
  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      return;
    }
    const selectedKey = Array.from(keys)[0];
    if (selectedKey) {
      const entity =
        flatItems.find(item => item.entity_id === selectedKey) ??
        recentSearches.find(item => item.entity_id === selectedKey);
      if (entity) {
        // Check if this was a recent search selection
        const isRecentSearch = recentSearches.some(
          item => item.entity_id === selectedKey
        );
        if (isRecentSearch) {
          logger.info('Recent search selected', {
            entityId: entity.entity_id,
            component: 'GlobalSearch',
          });
        }
        handleSelect(entity);
      }
    }
  };

  // Filter chip handlers
  const handleFilterClick = (type: EntityType | 'all') => {
    // Compute the resulting filter state (not just the clicked type)
    let resultingFilter: EntityType | 'all';
    if (type === 'all') {
      resultingFilter = 'all';
    } else if (selectedTypes?.length === 1 && selectedTypes[0] === type) {
      // Toggling off a selected filter returns to 'all'
      resultingFilter = 'all';
    } else {
      resultingFilter = type;
    }

    logger.info('Search filter changed', {
      filter: resultingFilter,
      component: 'GlobalSearch',
    });
    track('global_search_filter_changed', { filter: resultingFilter });

    if (type === 'all') {
      setSelectedTypes(undefined);
    } else {
      // Single-select: toggle if already selected, otherwise select
      if (selectedTypes?.length === 1 && selectedTypes[0] === type) {
        setSelectedTypes(undefined);
      } else {
        setSelectedTypes([type]);
      }
    }
  };

  const isFilterActive = (type: EntityType | 'all') => {
    if (type === 'all') {
      return selectedTypes === undefined;
    }
    return selectedTypes?.length === 1 && selectedTypes[0] === type;
  };

  // Helper to get type label for aria-label
  const getTypeLabel = (type: EntityType) => {
    if (type === 'ahoy_contact') {
      return 'Contacts';
    }
    if (type === 'ahoy_company') {
      return 'Companies';
    }
    return 'Deals';
  };

  // Render content based on state
  const renderContent = () => {
    // Loading state
    if (isLoading) {
      return (
        <div className='flex items-center justify-center py-12'>
          <LoadingIndicator size='md' />
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className='flex flex-col items-center justify-center py-12 gap-3'>
          <AlertCircle
            className='size-8'
            style={{ color: 'var(--color-text-error-primary)' }}
          />
          <div
            className='text-sm'
            style={{ color: 'var(--color-text-secondary)' }}
          >
            We couldn&apos;t load search results. Try again.
          </div>
          <button
            onClick={refetch}
            aria-label='Retry search'
            className='px-3 py-1 text-sm rounded-md'
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-primary)',
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    // Empty state (no query)
    if (query.length < 2) {
      if (recentSearches.length > 0) {
        return (
          <AriaListBox
            aria-label='Recent searches'
            selectionMode='single'
            onSelectionChange={handleSelectionChange}
            className='outline-none'
          >
            <AriaSection>
              <AriaHeader
                className='px-3 py-2 text-xs font-medium uppercase tracking-wider'
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                Recent
              </AriaHeader>
              {recentSearches.map(entity => (
                <AriaListBoxItem
                  key={entity.entity_id}
                  id={entity.entity_id}
                  textValue={entity.name}
                  className='flex items-center gap-3 px-3 py-2 cursor-pointer outline-none data-[focused]:bg-[var(--color-bg-tertiary)]'
                >
                  {(() => {
                    const IconComponent = getEntityTypeIconComponent(
                      entity.type
                    );
                    return <IconComponent className='size-4 shrink-0' />;
                  })()}
                  <div className='flex-1 min-w-0'>
                    <div
                      className='font-medium truncate'
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {entity.name}
                    </div>
                    {entity.secondary_text && (
                      <div
                        className='text-sm truncate'
                        style={{ color: 'var(--color-text-secondary)' }}
                        title={entity.secondary_text}
                      >
                        {entity.secondary_text}
                      </div>
                    )}
                  </div>
                </AriaListBoxItem>
              ))}
            </AriaSection>
          </AriaListBox>
        );
      }

      return (
        <div className='flex flex-col items-center justify-center py-12 gap-3'>
          <SearchLg
            className='size-8'
            style={{ color: 'var(--color-text-tertiary)' }}
          />
          <div
            className='text-sm'
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Search contacts, companies, and deals
          </div>
        </div>
      );
    }

    // No results state
    if (groupedItems.length === 0) {
      return (
        <div className='flex flex-col items-center justify-center py-12 gap-3'>
          <SearchLg
            className='size-8'
            style={{ color: 'var(--color-text-tertiary)' }}
          />
          <div
            className='text-sm'
            style={{ color: 'var(--color-text-secondary)' }}
          >
            No results for &quot;{query}&quot;
          </div>
        </div>
      );
    }

    // Results state
    return (
      <AriaListBox
        aria-label={
          selectedTypes?.length === 1
            ? `${getTypeLabel(selectedTypes[0])} results`
            : 'Search results'
        }
        selectionMode='single'
        onSelectionChange={handleSelectionChange}
        className='outline-none'
      >
        {groupedItems.map(group => (
          <AriaSection key={group.type}>
            <AriaHeader
              className='px-3 py-2 text-xs font-medium uppercase tracking-wider'
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              {group.label}
            </AriaHeader>
            {group.items.map(entity => (
              <AriaListBoxItem
                key={entity.entity_id}
                id={entity.entity_id}
                textValue={entity.name}
                className='flex items-center gap-3 px-3 py-2 cursor-pointer outline-none data-[focused]:bg-[var(--color-bg-tertiary)]'
              >
                {(() => {
                  const IconComponent = getEntityTypeIconComponent(entity.type);
                  return <IconComponent className='size-4 shrink-0' />;
                })()}
                <div className='flex-1 min-w-0'>
                  <div
                    className='font-medium truncate'
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {entity.name}
                  </div>
                  {entity.secondary_text && (
                    <div
                      className='text-sm truncate'
                      style={{ color: 'var(--color-text-secondary)' }}
                      title={entity.secondary_text}
                    >
                      {entity.secondary_text}
                    </div>
                  )}
                </div>
              </AriaListBoxItem>
            ))}
          </AriaSection>
        ))}
      </AriaListBox>
    );
  };

  return (
    <AriaModalOverlay
      isOpen={isOpen}
      isDismissable
      onOpenChange={open => {
        if (!open) {
          close();
        }
      }}
      className='fixed inset-0 flex items-center justify-start flex-col pt-[15vh]'
      style={{ zIndex }}
    >
      <AriaModal className='w-full max-w-2xl data-[entering]:animate-in data-[entering]:fade-in data-[entering]:zoom-in-95 data-[exiting]:animate-out data-[exiting]:fade-out data-[exiting]:zoom-out-95 duration-150'>
        <AriaDialog
          aria-label='Search objects'
          className='outline-none rounded-xl shadow-xl overflow-hidden'
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            border: '1px solid var(--color-border-primary)',
          }}
        >
          {/* Search input */}
          <div
            className='px-4 py-3 flex items-center gap-3'
            style={{ borderBottom: '1px solid var(--color-border-primary)' }}
          >
            <SearchLg
              className='size-5 shrink-0'
              style={{ color: 'var(--color-text-tertiary)' }}
            />
            <input
              type='text'
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder='Search contacts, companies, and deals'
              aria-label='Search contacts, companies, and deals'
              autoFocus
              className='flex-1 outline-none bg-transparent text-base'
              style={{ color: 'var(--color-text-primary)' }}
            />
          </div>

          {/* Filter chips */}
          <div
            role='group'
            aria-label='Filter results by object type'
            className='px-4 py-3 flex items-center gap-2'
            style={{ borderBottom: '1px solid var(--color-border-primary)' }}
          >
            <button
              onClick={() => handleFilterClick('all')}
              aria-label='All results'
              aria-pressed={isFilterActive('all')}
              className={cx(
                'rounded-full px-3 py-1 text-sm font-medium cursor-pointer transition-colors flex items-center gap-1',
                isFilterActive('all')
                  ? 'bg-[var(--color-brand-600)] text-white'
                  : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]'
              )}
            >
              {isFilterActive('all') && <Check className='size-4' />}
              All
            </button>
            <button
              onClick={() => handleFilterClick('ahoy_contact')}
              aria-label='Contacts only'
              aria-pressed={isFilterActive('ahoy_contact')}
              className={cx(
                'rounded-full px-3 py-1 text-sm font-medium cursor-pointer transition-colors flex items-center gap-1',
                isFilterActive('ahoy_contact')
                  ? 'bg-[var(--color-brand-600)] text-white'
                  : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]'
              )}
            >
              {isFilterActive('ahoy_contact') && <Check className='size-4' />}
              Contacts
            </button>
            <button
              onClick={() => handleFilterClick('ahoy_company')}
              aria-label='Companies only'
              aria-pressed={isFilterActive('ahoy_company')}
              className={cx(
                'rounded-full px-3 py-1 text-sm font-medium cursor-pointer transition-colors flex items-center gap-1',
                isFilterActive('ahoy_company')
                  ? 'bg-[var(--color-brand-600)] text-white'
                  : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]'
              )}
            >
              {isFilterActive('ahoy_company') && <Check className='size-4' />}
              Companies
            </button>
            <button
              onClick={() => handleFilterClick('ahoy_deal')}
              aria-label='Deals only'
              aria-pressed={isFilterActive('ahoy_deal')}
              className={cx(
                'rounded-full px-3 py-1 text-sm font-medium cursor-pointer transition-colors flex items-center gap-1',
                isFilterActive('ahoy_deal')
                  ? 'bg-[var(--color-brand-600)] text-white'
                  : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]'
              )}
            >
              {isFilterActive('ahoy_deal') && <Check className='size-4' />}
              Deals
            </button>
          </div>

          {/* Results area */}
          <div className='max-h-[60vh] overflow-y-auto'>{renderContent()}</div>

          {/* Footer hint */}
          <div
            className='px-4 py-2 text-xs text-center'
            style={{
              color: 'var(--color-text-tertiary)',
              borderTop: '1px solid var(--color-border-primary)',
            }}
          >
            ESC to close
          </div>
        </AriaDialog>
      </AriaModal>
    </AriaModalOverlay>
  );
}
